import { SearchSortDirection } from "@shopliftr/common-js/data";
import { IListSortOption } from "../interfaces/list-sort-option";

/**
 * Utility class for client-side list sorting operations.
 *
 * Provides helpers for extracting sort fields, building comparator functions
 * from an {@link IListSortOption}, and detecting whether a model update touches
 * any of the sort-relevant fields.
 */
export class ListSortUtil {

    /**
     * Returns the field paths from the option's `searchSort` array.
     *
     * @param option - The sort option to inspect.
     * @returns An array of field path strings (may be empty).
     */
    static sortFields(option: IListSortOption): Array<string> {
        return (option.searchSort ?? []).map((s) => s.field);
    }

    /**
     * Builds a comparator function for the given sort option.
     *
     * Resolution order:
     * 1. If `option.compare` is provided, delegates to it.
     * 2. If `option.searchSort` has entries, derives a comparator from those fields.
     * 3. Otherwise returns `undefined` (no ordering available).
     *
     *    Null / undefined values are always sorted last regardless of direction.
     *    String values are compared via `localeCompare`; all other values use
     *    standard relational operators.
     *
     * @param option - The sort option to build a comparator for.
     * @returns A comparator `(a, b) => number`, or `undefined` when no ordering
     * can be derived.
     */
    static comparator<M>(option: IListSortOption): ((a: M, b: M) => number) | undefined {
        if (option.compare) {
            return (a, b) => option.compare!(a, b);
        }
        if (!option.searchSort?.length) {
            return undefined;
        }
        return (a: M, b: M): number => {
            for (const sortField of option.searchSort!) {
                const direction = sortField.direction === SearchSortDirection.Descending ? -1 : 1;
                const aVal = ListSortUtil._resolve(a, sortField.field);
                const bVal = ListSortUtil._resolve(b, sortField.field);
                if (aVal === undefined || aVal === null) {
                    return 1;
                }
                if (bVal === undefined || bVal === null) {
                    return -1;
                }
                let cmp = 0;
                if (typeof aVal === "string" && typeof bVal === "string") {
                    cmp = aVal.localeCompare(bVal);
                }
                else {
                    cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                }
                if (cmp !== 0) {
                    return cmp * direction;
                }
            }
            return 0;
        };
    }

    /**
     * Returns `true` when at least one of the option's sort fields has a
     * different value between `original` and `updated`.
     *
     * @param option - The sort option whose fields to compare.
     * @param original - The original model instance.
     * @param updated - The updated model instance.
     * @returns `true` if any sort field changed; `false` otherwise.
     */
    static sortFieldChanged<M>(option: IListSortOption, original: M, updated: M): boolean {
        return ListSortUtil.sortFields(option).some(
            (field) => ListSortUtil._resolve(original, field) !== ListSortUtil._resolve(updated, field)
        );
    }

    /**
     * Resolves a dotted-path property accessor against an object.
     *
     * @param obj - The root object.
     * @param path - A property path such as `"chain.name"`.
     * @returns The resolved value, or `undefined` if any segment is missing.
     */
    private static _resolve(obj: unknown, path: string): unknown {
        return path.split(".").reduce((acc, key) => (acc as Record<string, unknown>)?.[key], obj);
    }
}
